import API, { TGetObjectNamesRes } from "./TimeEditAPI";

type CacheKey = string;
type Listener = () => void;

type TPrefetchItem = { id: number; type: number };

const NO_VALUE_PLACEHOLDER = "-";

const cache: Map<CacheKey, string> = new Map();
const listeners: Set<Listener> = new Set();

const keyOf = (typeId: number, objectId: number): CacheKey => `${typeId}:${objectId}`;

const notify = (): void => {
    listeners.forEach((listener) => listener());
};

const extractName = (response: TGetObjectNamesRes): string => {
    const field = response.fields && response.fields[0];
    if (!field?.values?.length) {
        return NO_VALUE_PLACEHOLDER;
    }
    return field.values.join(", ");
};

const get = (typeId: number, objectId: number): string | undefined => {
    if (objectId === 0) {
        return undefined;
    }
    return cache.get(keyOf(typeId, objectId));
};

const set = (typeId: number, objectId: number, name: string): void => {
    if (objectId === 0) {
        return;
    }
    cache.set(keyOf(typeId, objectId), name);
    notify();
};

const setMany = (entries: { typeId: number; objectId: number; name: string }[]): void => {
    let changed = false;
    entries.forEach(({ typeId, objectId, name }) => {
        if (!objectId) {
            return;
        }
        cache.set(keyOf(typeId, objectId), name);
        changed = true;
    });
    if (changed) {
        notify();
    }
};

const invalidate = (typeId: number): void => {
    const prefix = `${typeId}:`;
    let changed = false;
    cache.forEach((_value, key) => {
        if (key.startsWith(prefix)) {
            cache.delete(key);
            changed = true;
        }
    });
    if (changed) {
        notify();
    }
};

const populate = (typeByObjectId: Map<number, number>, responses: TGetObjectNamesRes[]): void => {
    let changed = false;
    responses.forEach((result) => {
        const typeId = typeByObjectId.get(result.id);
        if (typeId === undefined || result.id === 0) {
            return;
        }
        cache.set(keyOf(typeId, result.id), extractName(result));
        changed = true;
    });
    if (changed) {
        notify();
    }
};

const fetchAndPopulate = (
    items: TPrefetchItem[],
    callback: (responses: TGetObjectNamesRes[]) => void = () => {}
): void => {
    const typeByObjectId = new Map<number, number>(items.map((item) => [item.id, item.type]));
    API.getObjectNames(items, false, (responses) => {
        populate(typeByObjectId, responses);
        callback(responses);
    });
};

const prefetch = (items: TPrefetchItem[], callback: () => void = () => {}): void => {
    const missing = items.filter((item) => item.id !== 0 && !cache.has(keyOf(item.type, item.id)));
    if (missing.length === 0) {
        callback();
        return;
    }
    fetchAndPopulate(missing, () => callback());
};

const subscribe = (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

export const _clearForTests = (): void => {
    cache.clear();
    listeners.clear();
};

export default {
    get,
    set,
    setMany,
    populate,
    prefetch,
    fetchAndPopulate,
    invalidate,
    subscribe,
};
